import React, { useState, useEffect, useMemo } from 'react';
import { LogOut, ChevronDown, ChevronUp, Phone, MapPin, MessageCircle, Navigation } from 'lucide-react';
import { LOGO_URL } from '../utils/assets';
import { MARK } from '../constants';
import { conversaDoWhatsapp } from '../data/cliente';
import {
  minhaAgenda, minhaEtapa, agendaEmDias, rotuloDoDia, ROTULO_DA_ETAPA, coordenadasDoMapa,
} from '../data/tecnico';
import { linkDoMapa, linkDoWaze, precisaResolver } from '../utils/localizacao';

/**
 * Agenda do técnico: o que ele vai fazer, só para ler, feita para celular.
 *
 * Tela própria, não a área do cliente nem o app do laboratório com campos
 * escondidos — pela mesma razão da área do cliente: esconder é frágil. Aqui só
 * existe o que o banco manda, e o banco só manda as etapas em que ele está
 * escalado e que ainda vão acontecer. Nada de valor, nada de resultado de
 * etapa passada.
 *
 * A agenda é uma LISTA POR DIA, como a visão "programação" de agenda de
 * celular: num telefone, sete colunas de semana viram sete tiras ilegíveis, e
 * a pergunta de quem abre é "aonde eu vou e quando", que uma lista responde de
 * cima para baixo.
 *
 * Tocar num trabalho abre a ficha do cliente (com quem falar, onde é) e o que
 * entra na etapa — a mesma regra da ficha de campo: as receptoras da DG, os
 * embriões da TE, as doadoras da FIV.
 */

const hojeISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const dataCurta = (iso) => {
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'UTC' });
};

const limpo = (v) => String(v || '').trim();

/* ------------------------------------------------------------------ */
/* Ficha do cliente                                                    */
/* ------------------------------------------------------------------ */

const FichaDoCliente = ({ cliente }) => {
  const localizacao = limpo(cliente?.localizacao);

  // Link curto: a coordenada do Waze vem do servidor, ao abrir a ficha — e
  // não no toque, porque celular bloqueia a aba aberta depois de uma espera.
  const [coordenadas, setCoordenadas] = useState(null);
  useEffect(() => {
    if (!precisaResolver(localizacao)) return undefined;
    let vivo = true;
    coordenadasDoMapa(localizacao).then(c => { if (vivo) setCoordenadas(c); });
    return () => { vivo = false; };
  }, [localizacao]);

  if (!cliente) return null;
  const mapa = linkDoMapa(localizacao);
  const waze = linkDoWaze(localizacao, coordenadas);
  const telefone = limpo(cliente.telefone);
  const conversa = conversaDoWhatsapp(telefone);
  const endereco = [cliente.endereco, cliente.cidade, cliente.uf, cliente.cep].map(limpo).filter(Boolean).join(' · ');

  return (
    <section>
      <h4 className="text-xs uppercase tracking-wide text-slate-400 mb-1.5">Cliente</h4>
      <p className="text-sm font-medium text-slate-900">{limpo(cliente.nome)}</p>
      {limpo(cliente.fazenda) && <p className="text-sm text-slate-700">{limpo(cliente.fazenda)}</p>}
      {limpo(cliente.contato) && <p className="text-sm text-slate-600">Falar com {limpo(cliente.contato)}</p>}
      {endereco && <p className="text-sm text-slate-600 mt-0.5">{endereco}</p>}

      {/* Botões grandes: é tela de quem está dirigindo para a fazenda. */}
      {(telefone || mapa) && (
        <div className="flex flex-wrap gap-2 mt-2.5">
          {telefone && (
            <a href={`tel:${telefone.replace(/[^\d+]/g, '')}`}
              className="inline-flex items-center gap-1.5 min-h-[40px] px-3 rounded-lg border border-slate-200
                         text-sm text-slate-700 hover:bg-slate-50">
              <Phone size={15} /> {telefone}
            </a>
          )}
          {conversa && (
            <a href={conversa} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 min-h-[40px] px-3 rounded-lg border border-slate-200
                         text-sm text-slate-700 hover:bg-slate-50">
              <MessageCircle size={15} /> WhatsApp
            </a>
          )}
          {mapa && (
            <a href={mapa} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 min-h-[40px] px-3 rounded-lg border border-slate-200
                         text-sm text-slate-700 hover:bg-slate-50">
              <MapPin size={15} /> Como chegar
            </a>
          )}
          {waze && (
            <a href={waze} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 min-h-[40px] px-3 rounded-lg border border-slate-200
                         text-sm text-slate-700 hover:bg-slate-50">
              <Navigation size={15} /> Waze
            </a>
          )}
        </div>
      )}
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* O que entra na etapa                                                */
/* ------------------------------------------------------------------ */

const Entrada = ({ etapa, entrada }) => {
  if (!Array.isArray(entrada)) return null;

  if (etapa === 'dg30' || etapa === 'dg60') {
    return (
      <section>
        <h4 className="text-xs uppercase tracking-wide text-slate-400 mb-1.5">
          Receptoras a examinar · {entrada.length}
        </h4>
        {entrada.length === 0
          ? <p className="text-sm text-slate-500">Nenhuma receptora lançada ainda.</p>
          : (
            <ul className="flex flex-wrap gap-1.5">
              {entrada.map((r, i) => (
                <li key={`${r}-${i}`} className="px-2 py-1 rounded-md bg-slate-100 text-sm text-slate-800">{r}</li>
              ))}
            </ul>
          )}
      </section>
    );
  }

  if (etapa === 'te') {
    return (
      <section>
        <h4 className="text-xs uppercase tracking-wide text-slate-400 mb-1.5">
          Embriões disponíveis · {entrada.length}
        </h4>
        {entrada.length === 0
          ? <p className="text-sm text-slate-500">A FIV ainda não lançou embriões.</p>
          : (
            <ul className="divide-y divide-slate-100 text-sm">
              {entrada.map((e, i) => (
                <li key={`${e.numero}-${i}`} className="py-1.5 flex gap-2">
                  <span className="w-8 shrink-0 tabular-nums text-slate-400">{e.numero ?? '—'}</span>
                  <span className="min-w-0 text-slate-800">
                    {limpo(e.doadora) || 'Doadora?'} × {limpo(e.touro) || 'Touro?'}
                    <span className="text-slate-500">
                      {[e.estagio, e.destino === 'frozen' ? 'vitrificado' : 'fresco'].filter(Boolean).map(v => ` · ${v}`)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
      </section>
    );
  }

  if (etapa === 'fiv') {
    return (
      <section>
        <h4 className="text-xs uppercase tracking-wide text-slate-400 mb-1.5">
          Doadoras da OPU · {entrada.length}
        </h4>
        {entrada.length === 0
          ? <p className="text-sm text-slate-500">A OPU ainda não foi lançada.</p>
          : (
            <ul className="divide-y divide-slate-100 text-sm">
              {entrada.map((d, i) => (
                <li key={`${d.doadora}-${i}`} className="py-1.5 flex justify-between gap-3">
                  <span className="min-w-0 text-slate-800">
                    {limpo(d.doadora)} × {limpo(d.touro) || 'Touro?'}
                  </span>
                  {d.viaveis != null && <span className="shrink-0 tabular-nums text-slate-500">{d.viaveis} viáveis</span>}
                </li>
              ))}
            </ul>
          )}
      </section>
    );
  }

  return null;
};

/* ------------------------------------------------------------------ */
/* Um trabalho na agenda                                               */
/* ------------------------------------------------------------------ */

const Trabalho = ({ evento, aberto, onAbrir, detalhe, atrasado }) => {
  const local = [evento.fazenda, evento.cidade].map(limpo).filter(Boolean).join(' · ');
  const nReceptoras = Array.isArray(evento.receptoras) ? evento.receptoras.length : null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <button type="button" onClick={onAbrir} aria-expanded={aberto}
        className="w-full min-h-[64px] px-4 py-3 flex items-start gap-3 text-left hover:bg-slate-50">
        <span className="w-20 shrink-0">
          <span className="block text-sm font-semibold text-slate-900">{ROTULO_DA_ETAPA[evento.tipo] || evento.tipo}</span>
          {atrasado && <span className="block text-xs text-slate-600">{MARK.WARN} {dataCurta(evento.data)}</span>}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm text-slate-900 truncate">{limpo(evento.cliente)}</span>
          {local && <span className="block text-xs text-slate-500 truncate">{local}</span>}
          <span className="block text-xs text-slate-500">
            {[
              evento.parceiro ? `com ${evento.parceiro}` : null,
              evento.papel === 'ajudante' ? 'você ajuda' : null,
              nReceptoras ? `${nReceptoras} receptoras` : null,
              evento.service_code ? `#${evento.service_code}` : null,
            ].filter(Boolean).join(' · ')}
          </span>
        </span>
        {aberto
          ? <ChevronUp className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
          : <ChevronDown className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />}
      </button>

      {aberto && (
        <div className="px-4 pb-4 pt-1 border-t border-slate-100 space-y-4">
          {evento.observacao && (
            <section>
              <h4 className="text-xs uppercase tracking-wide text-slate-400 mb-1">Observação</h4>
              <p className="text-sm text-slate-700 whitespace-pre-line">{evento.observacao}</p>
            </section>
          )}
          {!detalhe && <p className="text-sm text-slate-500">Carregando...</p>}
          {detalhe?.erro && <p className="text-sm text-slate-600">Não foi possível abrir: {detalhe.erro}</p>}
          {detalhe && !detalhe.erro && (
            <>
              <Entrada etapa={evento.etapa} entrada={detalhe.entrada} />
              <FichaDoCliente cliente={detalhe.cliente} />
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */

const Grupo = ({ titulo, children }) => (
  <section>
    <h3 className="text-sm font-medium text-slate-700 mb-2 first-letter:uppercase">{titulo}</h3>
    <div className="space-y-2">{children}</div>
  </section>
);

const AreaTecnico = ({ cadastro, sessao, onSair }) => {
  const [linhas, setLinhas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [aberto, setAberto] = useState(null);
  const [detalhes, setDetalhes] = useState({});
  const hoje = hojeISO();

  useEffect(() => {
    let cancelado = false;
    minhaAgenda(sessao)
      .then(l => { if (!cancelado) setLinhas(l || []); })
      .catch(e => { if (!cancelado) setErro(e.message); })
      .finally(() => { if (!cancelado) setCarregando(false); });
    return () => { cancelado = true; };
  }, [sessao]);

  const { atrasadas, dias, semData } = useMemo(() => agendaEmDias(linhas, hoje), [linhas, hoje]);
  const vazia = !atrasadas.length && !dias.length && !semData.length;

  // A retirada abre a etapa do protocolo, então o detalhe é por serviço+etapa
  // e serve aos dois eventos.
  const chaveDoDetalhe = (e) => `${e.service_id}:${e.etapa}`;
  const chaveDoEvento = (e) => `${e.service_id}:${e.tipo}`;

  const abrir = async (evento) => {
    const k = chaveDoEvento(evento);
    if (aberto === k) { setAberto(null); return; }
    setAberto(k);

    const d = chaveDoDetalhe(evento);
    if (detalhes[d]) return;
    try {
      const r = await minhaEtapa(sessao, evento.service_id, evento.etapa);
      setDetalhes(prev => ({ ...prev, [d]: r || { erro: 'esta etapa não está mais na sua agenda' } }));
    } catch (e) {
      setDetalhes(prev => ({ ...prev, [d]: { erro: e.message } }));
    }
  };

  const trabalho = (e, atrasado = false) => (
    <Trabalho key={chaveDoEvento(e)} evento={e} atrasado={atrasado}
      aberto={aberto === chaveDoEvento(e)} onAbrir={() => abrir(e)}
      detalhe={detalhes[chaveDoDetalhe(e)]} />
  );

  return (
    <div className="min-h-[100dvh] bg-slate-50">
      <header className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src={LOGO_URL()} alt="" className="h-9 w-auto shrink-0" />
            <div className="min-w-0">
              <p className="font-semibold text-slate-900 truncate leading-tight">{cadastro?.tecnico}</p>
              <p className="text-xs text-slate-500 truncate">{cadastro?.laboratorio}</p>
            </div>
          </div>
          <button onClick={onSair} title="Sair"
            className="p-3 -mr-1 text-slate-400 hover:text-slate-700 rounded-lg shrink-0">
            <LogOut size={20} />
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5 space-y-6 pb-16">
        {carregando && <p className="text-sm text-slate-500 py-10 text-center">Carregando...</p>}

        {erro && (
          <div className="bg-white border border-slate-200 rounded-2xl p-4 text-sm text-slate-700">
            Não foi possível carregar: {erro}
          </div>
        )}

        {!carregando && !erro && vazia && (
          <div className="bg-white border border-slate-200 rounded-2xl py-14 text-center text-slate-500">
            <p className="text-sm">Nenhum trabalho marcado para você.</p>
          </div>
        )}

        {atrasadas.length > 0 && (
          <Grupo titulo={`${MARK.WARN} Data passou e ainda não foi lançado`}>
            {atrasadas.map(e => trabalho(e, true))}
          </Grupo>
        )}

        {dias.map(({ data, eventos }) => (
          <Grupo key={data} titulo={rotuloDoDia(data, hoje)}>
            {eventos.map(e => trabalho(e))}
          </Grupo>
        ))}

        {semData.length > 0 && (
          <Grupo titulo="Sem data marcada">
            {semData.map(e => trabalho(e))}
          </Grupo>
        )}
      </main>
    </div>
  );
};

export default AreaTecnico;
